import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { SearchPageCombinedState, SearchPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { SearchPageDataContextProvider } from './pageData';
import { SearchPageFcmContextProvider } from './pageFcm';

export type ErrorHandlerProps = { context: ErrorStateDetails<SearchPageCombinedState> };

type Props = {
	prefetchedData: SearchPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function SearchPageContextProvider({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageFcm } = pageContextState;

	return (
		<SearchPageDataContextProvider pageData={pageData}>
			<SearchPageFcmContextProvider pageFcm={pageFcm}>
				<TrackingContext
					properties={{
						// TODO: Add tracking properties
						search_version: 'v2',
					}}
				>
					{children}
				</TrackingContext>
			</SearchPageFcmContextProvider>
		</SearchPageDataContextProvider>
	);
}
